import { MutableRefObject, useCallback, useEffect, useLayoutEffect, useState } from 'react';

import * as THREE from 'three';

interface AvatarShellStateRef {
  getMixer?: () => THREE.AnimationMixer | null;
  getMorphTargetDictionary?: () => { body: Record<string, number>; teeth: Record<string, number> } | null;
  isReady?: () => boolean;
}

export interface UseAvatarShellStateOptions {
  activeAvatarModelId: string;
  activeAvatarModelName: string;
  activeAvatarModelUrl: string;
  avatarRef: MutableRefObject<AvatarShellStateRef | null>;
  canvasContainerRef: MutableRefObject<HTMLDivElement | null>;
  onAvatarModelReady?: () => void;
  verboseAvatarDebug: boolean;
}

export interface UseAvatarShellStateReturn {
  avatarLoadingMessage: string | null;
  canvasContainerSize: { width: number; height: number } | null;
  isAvatarReady: boolean;
  markAvatarLoading: (message: string) => void;
  markAvatarReady: () => void;
}

export function useAvatarShellState({
  activeAvatarModelId,
  activeAvatarModelName,
  activeAvatarModelUrl,
  avatarRef,
  canvasContainerRef,
  onAvatarModelReady,
  verboseAvatarDebug
}: UseAvatarShellStateOptions): UseAvatarShellStateReturn {
  const [isAvatarReady, setIsAvatarReady] = useState(false);
  const [avatarLoadingMessage, setAvatarLoadingMessage] = useState<string | null>(null);
  const [canvasContainerSize, setCanvasContainerSize] = useState<{ width: number; height: number } | null>(null);

  const markAvatarReady = useCallback(() => {
    setIsAvatarReady(true);
    setAvatarLoadingMessage(null);
  }, []);

  const markAvatarLoading = useCallback((message: string) => {
    setIsAvatarReady(false);
    setAvatarLoadingMessage(message);
  }, []);

  useLayoutEffect(() => {
    const handleAvatarModelReady = () => {
      markAvatarReady();
      onAvatarModelReady?.();
    };

    window.addEventListener('avatar:modelReady', handleAvatarModelReady as EventListener);

    return () => {
      window.removeEventListener('avatar:modelReady', handleAvatarModelReady as EventListener);
    };
  }, [markAvatarReady, onAvatarModelReady]);

  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      return;
    }

    const updateSize = () => {
      const rect = container.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setCanvasContainerSize({ width: rect.width, height: rect.height });
      }
    };

    updateSize();
    const initialTimer = window.setTimeout(updateSize, 50);
    const observer = new ResizeObserver(() => {
      updateSize();
    });
    observer.observe(container);

    return () => {
      window.clearTimeout(initialTimer);
      observer.disconnect();
    };
  }, [canvasContainerRef]);

  useEffect(() => {
    markAvatarLoading(`Loading ${activeAvatarModelName}...`);
  }, [activeAvatarModelId, activeAvatarModelName, activeAvatarModelUrl, markAvatarLoading]);

  useEffect(() => {
    if (avatarRef.current && !isAvatarReady) {
      const isActuallyReady = typeof avatarRef.current.isReady === 'function'
        ? avatarRef.current.isReady()
        : false;

      if (isActuallyReady) {
        console.log('Avatar is now fully ready with all dependencies (our criteria)');
        markAvatarReady();
      } else {
        console.log('Avatar ref exists but not fully ready yet (missing dependencies)');
      }
    }
  }, [avatarRef, isAvatarReady, markAvatarReady]);

  useEffect(() => {
    const handleAvatarLoading = (event: CustomEvent) => {
      const message = event.detail?.message || 'Setting up your virtual assistant...';
      console.log('⏳ Avatar loading event:', message);
      setAvatarLoadingMessage(message);
    };

    const handleAvatarLoadingComplete = () => {
      console.log('✅ Avatar loading complete');
      setAvatarLoadingMessage(null);
    };

    window.addEventListener('avatar:loading', handleAvatarLoading as EventListener);
    window.addEventListener('avatar:loading:complete', handleAvatarLoadingComplete as EventListener);

    return () => {
      window.removeEventListener('avatar:loading', handleAvatarLoading as EventListener);
      window.removeEventListener('avatar:loading:complete', handleAvatarLoadingComplete as EventListener);
    };
  }, []);

  useEffect(() => {
    if (avatarRef.current && !isAvatarReady) {
      const hasMixer = !!avatarRef.current.getMixer?.();
      const morphDicts = avatarRef.current.getMorphTargetDictionary?.();
      const hasMorphDicts = !!morphDicts;
      const hasMorphBody = !!morphDicts?.body;
      const hasMorphTeeth = !!morphDicts?.teeth;
      const nativeIsReady = avatarRef.current.isReady?.() || false;

      if (verboseAvatarDebug) {
        console.log('Checking avatar readiness:', {
          hasAvatarRef: true,
          hasMixer,
          hasMorphDicts,
          hasMorphBody,
          hasMorphTeeth,
          nativeIsReady
        });
      }

      const isActuallyReady = hasMixer && hasMorphDicts && hasMorphBody && hasMorphTeeth;

      if (isActuallyReady) {
        if (verboseAvatarDebug) {
          console.log('Avatar is now fully ready with all dependencies (our criteria)');
        }
        setIsAvatarReady(true);
      } else if (verboseAvatarDebug) {
        console.log('Avatar ref exists but not fully ready yet (missing dependencies)');
      }
    }
  }, [avatarRef, isAvatarReady, verboseAvatarDebug]);

  return {
    avatarLoadingMessage,
    canvasContainerSize,
    isAvatarReady,
    markAvatarLoading,
    markAvatarReady
  };
}

export default useAvatarShellState;
